import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { Schema } from "@bw/shared/types/generated";
import { useEffect, useRef, useState } from "react";

type Match = Schema["TournamentOperatorMatch"];
type Score = Schema["TournamentScoreState"];
type ScoreCommand = Schema["TournamentScoreCommand"];
type ScoreTransition = Pick<
  Schema["TournamentExecutionCommand"],
  "idempotency_key" | "expected_revision"
> & {
  command: "start_play" | "enter_interval" | "resume_interval";
};
type FinalResult = Schema["TournamentFinalResult"];

const sideLabel = (side: "side1" | "side2") =>
  side === "side1" ? "Side 1" : "Side 2";

export function UmpireScorePanel({
  match,
  score,
  onScoreCommand,
  onExecutionCommand,
  onFinalize,
  onRefresh,
  loadError,
  onRetryLoad,
}: {
  match: Match;
  score: Score | null;
  onScoreCommand: (command: ScoreCommand) => Promise<Score>;
  onExecutionCommand: (command: ScoreTransition) => Promise<void>;
  onFinalize: (result: FinalResult) => Promise<void>;
  onRefresh: () => Promise<Score>;
  loadError?: string | null;
  onRetryLoad?: () => Promise<void>;
}) {
  const [current, setCurrent] = useState<Score | null>(score);
  const [serverSlot, setServerSlot] = useState<1 | 2>(1);
  const [receiverSlot, setReceiverSlot] = useState<1 | 2>(1);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryScoreCommand, setRetryScoreCommand] = useState<ScoreCommand | null>(
    null,
  );
  const pendingRef = useRef(false);

  useEffect(() => setCurrent(score), [score]);

  const executeScoreCommand = async (command: ScoreCommand) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    try {
      setCurrent(await onScoreCommand(command));
      setRetryScoreCommand(null);
    } catch (reason) {
      setRetryScoreCommand(command);
      setError(reason instanceof Error ? reason.message : "Score update failed");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const submitScore = (
    command: Omit<ScoreCommand, "idempotency_key" | "expected_revision">,
  ) => {
    if (!current) return;
    void executeScoreCommand({
      ...command,
      idempotency_key: crypto.randomUUID(),
      expected_revision: current.revision,
    } as ScoreCommand);
  };

  const refreshScore = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      setCurrent(await onRefresh());
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Score refresh failed");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const retryLoad = async () => {
    if (!onRetryLoad || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await onRetryLoad();
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const submitTransition = async (command: ScoreTransition["command"]) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    setRetryScoreCommand(null);
    try {
      await onExecutionCommand({
        command,
        idempotency_key: crypto.randomUUID(),
        expected_revision: match.execution.revision,
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Match update failed");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const finalize = async () => {
    if (!current?.match_complete || !current.winner_side || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    setRetryScoreCommand(null);
    try {
      await onFinalize({
        games: current.games,
        winner_side: current.winner_side,
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Result finalization failed");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const executionState = match.execution.state;
  const executionActions = match.execution.allowed_actions ?? [];
  const canInitialize = executionState === "warmup" && current?.revision === 0;
  const canStartPlay =
    executionState === "warmup" &&
    executionActions.includes("start_play") &&
    Boolean(current?.serving_side) &&
    (current?.revision ?? 0) > 0;
  const canEnterInterval =
    executionState === "in_progress" &&
    executionActions.includes("enter_interval");
  const canResumeInterval =
    executionState === "interval" &&
    executionActions.includes("resume_interval");
  const canFinalize =
    executionState === "in_progress" &&
    executionActions.includes("finalize_played") &&
    Boolean(current?.match_complete && current.winner_side);
  const canAward = executionState === "in_progress" && !current?.match_complete;
  const canCorrect = ["in_progress", "interval", "suspended"].includes(
    executionState,
  );

  return (
    <Paper component="section" aria-label="Umpire score panel" variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        <Typography component="h2" variant="h6">Umpire score panel</Typography>
        {!current ? (
          loadError ? (
            <Alert
              role="alert"
              severity="error"
              action={
                onRetryLoad ? (
                  <Button
                    disabled={pending}
                    size="small"
                    onClick={() => void retryLoad()}
                  >
                    Retry score load
                  </Button>
                ) : undefined
              }
            >
              {loadError}
            </Alert>
          ) : (
            <Typography role="status">Loading score…</Typography>
          )
        ) : (
          <>
            <Typography aria-label="Current game score" variant="h4">
              {current.current_game.side1}–{current.current_game.side2}
            </Typography>
            <Typography
              aria-label="Score update"
              aria-live="polite"
              color="text.secondary"
              role="status"
              variant="body2"
            >
              Score {current.current_game.side1}–{current.current_game.side2}. {current.serving_side
                ? `${sideLabel(current.serving_side)} serves`
                : "Set the initial server before scoring"}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Court end: {current.court_end ?? "—"}
            </Typography>
            {current.games.length ? (
              <Typography aria-label="Completed games" color="text.secondary" variant="body2">
                Completed games: {current.games.map((game) => `${game.side1}–${game.side2}`).join(", ")}
              </Typography>
            ) : null}
            {current.server_slot && current.receiver_slot ? (
              <Typography color="text.secondary" variant="body2">
                Server slot {current.server_slot}; receiver slot {current.receiver_slot}
              </Typography>
            ) : null}
            {error ? (
              <Alert
                role="alert"
                severity="error"
                action={
                  <Stack direction="row" spacing={0.5}>
                    {retryScoreCommand ? (
                      <Button
                        disabled={pending}
                        size="small"
                        onClick={() => void executeScoreCommand(retryScoreCommand)}
                      >
                        Retry score update
                      </Button>
                    ) : null}
                    <Button
                      disabled={pending}
                      size="small"
                      onClick={() => void refreshScore()}
                    >
                      Refresh score
                    </Button>
                  </Stack>
                }
              >
                {error}
              </Alert>
            ) : null}
            {canInitialize ? (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  <TextField
                    select
                    label="Server slot"
                    size="small"
                    value={serverSlot}
                    onChange={(event) => setServerSlot(Number(event.target.value) as 1 | 2)}
                  >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Receiver slot"
                    size="small"
                    value={receiverSlot}
                    onChange={(event) => setReceiverSlot(Number(event.target.value) as 1 | 2)}
                  >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={2}>2</MenuItem>
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  {(["side1", "side2"] as const).map((side) => (
                    <Button
                      key={side}
                      disabled={pending}
                      size="small"
                      onClick={() => void submitScore({
                        command: "set_initial_server",
                        serving_side: side,
                        server_slot: serverSlot,
                        receiver_slot: receiverSlot,
                      })}
                    >
                      {sideLabel(side)} serves
                    </Button>
                  ))}
                </Stack>
              </Stack>
            ) : null}
            {canStartPlay ? (
              <Button
                disabled={pending}
                size="small"
                variant="contained"
                onClick={() => void submitTransition("start_play")}
              >
                Start play
              </Button>
            ) : null}
            {canAward ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                {(["side1", "side2"] as const).map((side) => (
                  <Button
                    key={side}
                    disabled={pending}
                    size="small"
                    variant="contained"
                    onClick={() => void submitScore({
                      command: "award_rally",
                      winning_side: side,
                    })}
                  >
                    Point {sideLabel(side)}
                  </Button>
                ))}
              </Stack>
            ) : null}
            {canCorrect ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                {(["side1", "side2"] as const).map((side) => (
                  <Button
                    key={side}
                    disabled={pending}
                    size="small"
                    onClick={() => void submitScore({
                      command: "correct_last_rally",
                      replacement_winning_side: side,
                      reason: "correction_official_error",
                    })}
                  >
                    Correct last rally to {sideLabel(side)}
                  </Button>
                ))}
              </Stack>
            ) : null}
            {canEnterInterval ? (
              <Button disabled={pending} size="small" onClick={() => void submitTransition("enter_interval")}>
                Start interval
              </Button>
            ) : null}
            {executionState === "interval" ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Button disabled={pending} size="small" onClick={() => void submitScore({ command: "change_ends" })}>
                  Change ends
                </Button>
                {canResumeInterval ? (
                  <Button disabled={pending} size="small" onClick={() => void submitTransition("resume_interval")}>
                    Resume play
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            {current.match_complete && current.winner_side ? (
              <Typography aria-label="Match result" role="status" variant="body2">
                {sideLabel(current.winner_side)} wins
              </Typography>
            ) : null}
            {canFinalize ? (
              <Button disabled={pending} size="small" variant="contained" onClick={() => void finalize()}>
                Finalize server result
              </Button>
            ) : null}
          </>
        )}
      </Stack>
    </Paper>
  );
}
